import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import axios from "axios";
import { URL } from "../../../config/config";
import ConfirmationDialog from "../ConfirmationDialog";

// Fallbacks for when /capabilities has not resolved yet. Kept in sync with
// partner-rest-api's helpers/projectMessageEncryption.helper.ts — ECB is
// number-only (no per-request IV) and GCM is body-only (number mode never
// verifies an auth tag).
const FALLBACK_ALGORITHMS = {
  number: [
    "aes-128-cbc",
    "aes-192-cbc",
    "aes-256-cbc",
    "aes-128-ecb",
    "aes-192-ecb",
    "aes-256-ecb",
  ],
  body: [
    "aes-128-cbc",
    "aes-192-cbc",
    "aes-256-cbc",
    "aes-128-gcm",
    "aes-192-gcm",
    "aes-256-gcm",
  ],
};

const DEFAULT_SKEW = 300;
const MAX_SKEW = 600;

// The dashboard theme renders outlined inputs with a near-invisible border, so
// white selects vanish against the white panel. Force a visible outline.
const useFieldStyles = makeStyles({
  field: {
    background: "#fff",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#bdbdbd",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#757575",
    },
  },
});

export default function ProjectEncryption(props) {
  const { classes, assistantId, setSnackbar, hasWriteAccess } = props;
  const fieldClasses = useFieldStyles();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [capabilities, setCapabilities] = useState(null);

  // Existing config (secret is never returned on read).
  const [existing, setExisting] = useState(null);

  // Form state.
  const [encryptionType, setEncryptionType] = useState("number");
  const [algorithm, setAlgorithm] = useState("aes-256-cbc");
  const [secretSource, setSecretSource] = useState("random");
  const [inputFormat, setInputFormat] = useState("hex");
  const [secret, setSecret] = useState("");
  const [iv, setIv] = useState("");
  const [skewSeconds, setSkewSeconds] = useState(DEFAULT_SKEW);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Bounds come from /capabilities so the field can never offer a value the API
  // rejects; the fallbacks match the server's own limits.
  const skewMin = capabilities?.skew?.min || 60;
  const skewMax = capabilities?.skew?.max || MAX_SKEW;

  // Shown exactly once, right after create/rotate.
  const [generated, setGenerated] = useState(null);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const notify = useCallback(
    (alertMsg, alertType) =>
      setSnackbar?.({ alert: true, alertType, alertMsg }),
    [setSnackbar]
  );

  const algorithmsFor = useCallback(
    (mode) => {
      if (capabilities?.algorithms?.length) {
        return capabilities.algorithms
          .filter((a) => a.modes.includes(mode))
          .map((a) => a.algorithm);
      }
      return FALLBACK_ALGORITHMS[mode];
    },
    [capabilities]
  );

  const loadCapabilities = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${URL}/api/project-message-encryption/capabilities`
      );
      setCapabilities(data);
      if (data?.skew?.default) setSkewSeconds(data.skew.default);
    } catch (error) {
      // Non-fatal: the selects fall back to the built-in lists above.
      console.error("Failed to load encryption capabilities", error);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${URL}/api/project-message-encryption/${assistantId}`
      );
      setExisting(data);
      setEncryptionType(data.encryptionType || "number");
      setAlgorithm(data.algorithm);
      if (data.skewSeconds) setSkewSeconds(data.skewSeconds);
    } catch (error) {
      if (error?.response?.status === 404) {
        setExisting(null);
      } else {
        notify(
          error?.response?.data?.message || "Failed to load encryption config",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [assistantId, notify]);

  useEffect(() => {
    loadCapabilities();
    loadConfig();
  }, [loadCapabilities, loadConfig]);

  // Keep the algorithm valid for the selected mode — switching number -> body
  // with an ECB algorithm selected would otherwise post a combination the API
  // rejects.
  useEffect(() => {
    const allowed = algorithmsFor(encryptionType);
    if (!allowed.includes(algorithm)) setAlgorithm(allowed[0]);
  }, [encryptionType, algorithm, algorithmsFor]);

  const ivRequired =
    encryptionType === "number" &&
    !algorithm.endsWith("-ecb") &&
    secretSource === "custom";

  const buildBody = (action) => {
    // Rotate takes the mode from the stored config and rejects a mismatch —
    // switching modes has to go through disable + enable.
    const body =
      action === "rotate"
        ? { assistantId, algorithm }
        : { assistantId, algorithm, encryptionType };
    if (encryptionType === "body") body.skewSeconds = Number(skewSeconds);
    if (secretSource === "random") {
      body.generateRandom = true;
    } else {
      body.secret = secret.trim();
      body.inputFormat = inputFormat;
      if (ivRequired) body.iv = iv.trim();
    }
    return body;
  };

  const submit = async (action) => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${URL}/api/project-message-encryption/${action}`,
        buildBody(action)
      );
      setGenerated(data);
      setExisting({
        docId: data.docId,
        assistantId,
        encryptionType: data.encryptionType,
        algorithm: data.algorithm,
        skewSeconds: data.skewSeconds,
      });
      setSecret("");
      setIv("");
      notify(
        action === "rotate" ? "Encryption rotated" : "Encryption enabled",
        "success"
      );
    } catch (error) {
      notify(
        error?.response?.data?.message || "Failed to save encryption config",
        "error"
      );
    } finally {
      setSaving(false);
      setConfirmRotate(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await axios.delete(
        `${URL}/api/project-message-encryption/${assistantId}`
      );
      setExisting(null);
      setGenerated(null);
      notify("Encryption disabled", "success");
    } catch (error) {
      notify(
        error?.response?.data?.message || "Failed to delete encryption config",
        "error"
      );
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  const copy = (value) => {
    navigator.clipboard.writeText(value);
    notify("Copied", "success");
  };

  if (loading) {
    return (
      <Box textAlign="center" padding={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box padding={4}>
      <Typography variant="h4" color="primary" gutterBottom>
        Request Encryption
      </Typography>
      <FormHelperText style={{ marginBottom: 24 }}>
        Encrypt Project API requests for this project. The two modes are
        mutually exclusive — a project is either number mode or body mode.
      </FormHelperText>

      {existing && (
        <Box
          marginBottom={4}
          padding={2}
          style={{ background: "#f5f5f5", borderRadius: 4 }}
        >
          <Typography variant="body1">
            <b>Active:</b>{" "}
            {existing.encryptionType === "body"
              ? "Request Body & Query Encryption"
              : "Request Number Encryption"}{" "}
            · {existing.algorithm}
            {existing.skewSeconds ? ` · skew ${existing.skewSeconds}s` : ""}
          </Typography>
          <FormHelperText>
            Config ID {existing.docId} — send this as the{" "}
            {existing.encryptionType === "body"
              ? "x-encrypted-body"
              : "x-encrypted-number"}{" "}
            header.
          </FormHelperText>
        </Box>
      )}

      {generated && (
        <Box
          marginBottom={4}
          padding={2}
          style={{ border: "1px solid #ff8c00", borderRadius: 4 }}
        >
          <Typography variant="body1" style={{ fontWeight: "bold" }}>
            Save these now — the secret is shown once and cannot be recovered.
          </Typography>
          {[
            ["Config ID", generated.docId],
            ["Secret", generated.secret],
            ...(generated.iv ? [["IV", generated.iv]] : []),
          ].map(([label, value]) => (
            <Grid container alignItems="center" key={label}>
              <Grid item md={2} xs={4}>
                <Typography variant="body2">{label}</Typography>
              </Grid>
              <Grid item md={9} xs={7}>
                <Typography
                  variant="body2"
                  style={{ wordBreak: "break-all", fontFamily: "monospace" }}
                >
                  {value}
                </Typography>
              </Grid>
              <Grid item md={1} xs={1}>
                <IconButton size="small" onClick={() => copy(value)}>
                  <FileCopyIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="body1" style={{ fontWeight: "bold" }}>
            Encryption type
          </Typography>
          <RadioGroup
            value={encryptionType}
            onChange={(e) => {
              const mode = e.target.value;
              setEncryptionType(mode);
              // The two modes have different valid algorithm sets, so a stale
              // value would be rejected by the API. GCM is authenticated, which
              // is what body mode wants; number mode has no auth tag and needs
              // CBC.
              setAlgorithm(mode === "body" ? "aes-256-gcm" : "aes-256-cbc");
              setIv("");
            }}
          >
            <FormControlLabel
              value="number"
              control={<Radio color="primary" />}
              disabled={!hasWriteAccess || !!existing}
              label="Request Number Encryption"
            />
            <FormControlLabel
              value="body"
              control={<Radio color="primary" />}
              disabled={!hasWriteAccess || !!existing}
              label="Request Body & Query Encryption"
            />
          </RadioGroup>
          {existing && (
            <FormHelperText>
              The two modes are mutually exclusive. To switch, disable
              encryption first and enable it again in the other mode.
            </FormHelperText>
          )}
        </Grid>

        <Grid item md={4} xs={12}>
          <Typography variant="body1" gutterBottom>
            Algorithm
          </Typography>
          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            className={fieldClasses.field}
          >
            <Select
              value={algorithm}
              onChange={(e) => setAlgorithm(e.target.value)}
              disabled={!hasWriteAccess}
            >
              {algorithmsFor(encryptionType).map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                  {a.includes("-gcm") ? " · recommended" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {encryptionType === "body" && (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              style={{ color: "#1976d2", cursor: "pointer" }}
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? "\u25be" : "\u25b8"} Advanced
            </Typography>
            {showAdvanced && (
              <Box mt={1} maxWidth={260}>
                <Typography variant="body1" gutterBottom>
                  Replay window (seconds)
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  type="number"
                  className={fieldClasses.field}
                  value={skewSeconds}
                  disabled={!hasWriteAccess}
                  inputProps={{ min: skewMin, max: skewMax }}
                  // Digits only: a number input still accepts a minus sign, and
                  // a negative window is meaningless.
                  onChange={(e) =>
                    setSkewSeconds(e.target.value.replace(/[^0-9]/g, ""))
                  }
                />
                <FormHelperText>
                  How far a request timestamp may drift before it is rejected.
                  Allowed: {skewMin}–{skewMax} seconds.
                </FormHelperText>
              </Box>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom>
            Secret
          </Typography>
          <RadioGroup
            row
            value={secretSource}
            onChange={(e) => setSecretSource(e.target.value)}
          >
            <FormControlLabel
              value="random"
              control={<Radio color="primary" />}
              disabled={!hasWriteAccess}
              label="Generate for me"
            />
            <FormControlLabel
              value="custom"
              control={<Radio color="primary" />}
              disabled={!hasWriteAccess}
              label="Provide my own"
            />
          </RadioGroup>
        </Grid>

        {secretSource === "custom" && (
          <>
            <Grid item md={3} xs={12}>
              <Typography variant="body1" gutterBottom>
                Input format
              </Typography>
              <FormControl
                fullWidth
                variant="outlined"
                size="small"
                className={fieldClasses.field}
              >
                <Select
                  value={inputFormat}
                  onChange={(e) => setInputFormat(e.target.value)}
                  disabled={!hasWriteAccess}
                >
                  {(capabilities?.inputFormats || ["hex", "utf8"]).map(
                    (f) => (
                      <MenuItem key={f} value={f}>
                        {f}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item md={ivRequired ? 5 : 9} xs={12}>
              <Typography variant="body1" gutterBottom>
                Secret key
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={secret}
                className={fieldClasses.field}
                disabled={!hasWriteAccess}
                onChange={(e) => setSecret(e.target.value)}
              />
            </Grid>
            {ivRequired && (
              <Grid item md={4} xs={12}>
                <Typography variant="body1" gutterBottom>
                  IV
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={iv}
                  className={fieldClasses.field}
                  disabled={!hasWriteAccess}
                  onChange={(e) => setIv(e.target.value)}
                />
              </Grid>
            )}
          </>
        )}

        <Grid item xs={12}>
          <Box display="flex" style={{ gap: 12 }}>
            {!existing ? (
              <Button
                variant="contained"
                color="primary"
                disabled={!hasWriteAccess || saving}
                onClick={() => submit("create")}
              >
                {saving ? <CircularProgress size={22} /> : "Enable encryption"}
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!hasWriteAccess || saving}
                  onClick={() => setConfirmRotate(true)}
                >
                  Rotate secret
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  disabled={!hasWriteAccess || saving}
                  onClick={() => setConfirmDelete(true)}
                >
                  Disable encryption
                </Button>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {confirmRotate && (
        <ConfirmationDialog
          openDialog={confirmRotate}
          closeDialog={() => setConfirmRotate(false)}
          classes={classes}
          loader={saving}
          action={() => submit("rotate")}
          title="Rotate encryption secret?"
          body={
            <Typography variant="body1">
              The current secret stops working immediately. Any integration
              still using it will start failing until it is updated.
            </Typography>
          }
        />
      )}
      {confirmDelete && (
        <ConfirmationDialog
          openDialog={confirmDelete}
          closeDialog={() => setConfirmDelete(false)}
          classes={classes}
          loader={saving}
          action={remove}
          title="Disable encryption?"
          body={
            <Typography variant="body1">
              Requests will be accepted in plaintext again, and the stored
              secret is deleted permanently.
            </Typography>
          }
        />
      )}
    </Box>
  );
}
